import ClockInReminderToggle from "@/components/ClockInReminderToggle";
import { useCallback, useEffect, useState } from "react";
import { hasRole } from "@/lib/roles";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { silentFaceCapture, prewarmFaceModels } from "@/lib/silentFaceCapture";
import {
    MapPin, Clock, LogIn, LogOut, Building2, AlertTriangle,
    CheckCircle2, Loader2, RefreshCcw, ShieldCheck,
} from "lucide-react";

function getCurrentLocation() {
    return new Promise((resolve, reject) => {
        if (!("geolocation" in navigator)) {
            reject(new Error("Geolocation not supported on this device. Please use a modern browser."));
            return;
        }
        navigator.geolocation.getCurrentPosition(
            (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
            (err) => {
                const msg = err.code === 1
                    ? "Location permission was denied. Allow location access in your browser to check in."
                    : err.code === 3
                    ? "Couldn't get your location in time. Try again outdoors or with WiFi enabled."
                    : "We couldn't read your location. Please try again.";
                reject(new Error(msg));
            },
            { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 }
        );
    });
}

// Device clock: minutes east of UTC + IANA zone, sent with every check-in/out
function deviceTz() {
    let tz = "";
    try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ""; } catch { tz = ""; }
    return { tz_offset_minutes: -new Date().getTimezoneOffset(), timezone: tz };
}

// Show the time as it read on the device that clocked (stored local ISO), else browser-local
function fmtTime(iso, localIso) {
    if (localIso && localIso.length >= 16) return localIso.slice(11, 16);
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } catch {
        return iso;
    }
}

export default function AttendanceCheck() {
    const { user } = useAuth();
    const [worksites, setWorksites] = useState([]);
    const [todayState, setTodayState] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [coords, setCoords] = useState(null);
    const [locError, setLocError] = useState("");
    const [faceRequired, setFaceRequired] = useState(false);
    const [verifyStatus, setVerifyStatus] = useState(""); // "" | "locating" | "verifying" | "submitting"

    useEffect(() => {
        if (!hasRole(user, "employee") || !user?.employee_id) return;
        (async () => {
            try {
                const { data } = await api.get(`/employees/${user.employee_id}/face/status`);
                setFaceRequired(!!data?.enrolled);
                // If face is required, warm the face-api models in the background
                // so the eventual silent capture is fast.
                if (data?.enrolled) prewarmFaceModels();
            } catch {/* ignore */}
        })();
    }, [user]);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const [ws, td] = await Promise.all([
                api.get("/worksites").catch(() => ({ data: [] })),
                api.get("/attendance/today", { params: { tz_offset_minutes: deviceTz().tz_offset_minutes } }).catch(() => ({ data: { attendance: null } })),
            ]);
            setWorksites(ws.data || []);
            setTodayState(td.data);
        } catch {
            // ignore; today may 403 for non-employee viewing
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const fetchLocation = useCallback(async () => {
        setLocError("");
        try {
            const c = await getCurrentLocation();
            setCoords(c);
            return c;
        } catch (e) {
            setLocError(e.message);
            toast.error(e.message);
            return null;
        }
    }, []);

    const doCheck = async (kind) => {
        if (!hasRole(user, "employee") || !user?.employee_id) {
            toast.error("Only employees can check in/out. Admins manage attendance in the Attendance page.");
            return;
        }

        setBusy(true);
        try {
            // 1) Location
            setVerifyStatus("locating");
            const c = coords || (await fetchLocation());
            if (!c) { setVerifyStatus(""); return; }

            // 2) Silent face capture (only if employee has enrolled)
            let descriptor = null;
            if (faceRequired) {
                setVerifyStatus("verifying");
                try {
                    descriptor = await silentFaceCapture({ timeoutMs: 9000, maxAttempts: 8 });
                } catch (e) {
                    toast.error(e?.message || "Face verification failed. Please try again with better lighting.");
                    return;
                }
            }

            // 3) Submit
            setVerifyStatus("submitting");
            const body = { latitude: c.lat, longitude: c.lng, ...deviceTz() };
            if (descriptor) body.face_descriptor = descriptor;
            await api.post(`/attendance/check-${kind === "in" ? "in" : "out"}`, body);
            toast.success(kind === "in" ? "Checked in!" : "Checked out!");
            await refresh();
        } catch (e) {
            const msg = formatApiError(e.response?.data?.detail) || "Action failed";
            toast.error(msg);
        } finally {
            setBusy(false);
            setVerifyStatus("");
        }
    };

    const att = todayState?.attendance;
    const isEmployee = hasRole(user, "employee") && !!user?.employee_id;
    const checkedIn = !!att?.check_in_at;
    const checkedOut = !!att?.check_out_at;

    return (
        <div className="px-5 sm:px-8 md:px-12 py-8 sm:py-12 md:py-16" data-testid="attendance-check-page">
            <div className="flex items-end justify-between flex-wrap gap-6">
                <div>
                    <div className="overline">Daily Attendance</div>
                    <h1 className="mt-3 font-display font-black tracking-tighter text-4xl sm:text-5xl text-ink leading-[1.02]">
                        Check in & out
                    </h1>
                    <p className="mt-3 text-sm text-ink/60 max-w-xl">
                        We use your browser&apos;s location to confirm you&apos;re at an approved worksite, and a quick on-device face check to confirm it&apos;s you. Neither your coordinates nor your face image leave your device.
                    </p>
                    {user?.employee_id && <ClockInReminderToggle variant="light" className="mt-4" />}
                </div>
                <button
                    onClick={refresh}
                    className="border border-ink/15 px-4 py-2.5 hover:bg-ink/5 text-sm inline-flex items-center gap-2"
                    data-testid="attendance-refresh-btn"
                >
                    <RefreshCcw className="w-4 h-4" /> Refresh
                </button>
            </div>

            {!isEmployee && (
                <div className="mt-8 border border-amber-300 bg-amber-50/70 text-amber-900 px-4 py-3 text-sm" data-testid="attendance-not-employee-banner">
                    <AlertTriangle className="w-4 h-4 inline mr-2" />
                    You&apos;re signed in as <strong>{user?.role}</strong>. Geo check-in is for employees only. Admins/HR can mark attendance manually on the Attendance page.
                </div>
            )}

            <div className="mt-10 grid md:grid-cols-3 gap-6">
                {/* Big status card */}
                <div className="md:col-span-2 bg-white border border-ink/10 p-6 md:p-10">
                    {loading ? (
                        <div className="py-12 text-center text-sm text-ink/40">
                            <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
                        </div>
                    ) : (
                        <>
                            <div className="overline">Today · {todayState?.today || ""}</div>
                            <div className="mt-2 flex items-center gap-3">
                                {checkedOut ? (
                                    <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                                ) : checkedIn ? (
                                    <Clock className="w-8 h-8 text-klein" />
                                ) : (
                                    <Clock className="w-8 h-8 text-ink/30" />
                                )}
                                <div>
                                    <div className="font-display font-black text-2xl tracking-tighter">
                                        {checkedOut ? "Day complete" : checkedIn ? "Checked in" : "Not checked in"}
                                    </div>
                                    {att?.check_in_worksite_name && (
                                        <div className="text-sm text-ink/60 mt-1">
                                            <Building2 className="w-3.5 h-3.5 inline mr-1" />
                                            {att.check_in_worksite_name}
                                            {att.check_in_distance_m != null && (
                                                <span className="text-ink/40"> · within {Math.round(att.check_in_distance_m)}m</span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
                                <div className="border border-ink/10 p-4">
                                    <div className="overline">Check-in</div>
                                    <div className="font-display text-xl mt-1" data-testid="attendance-check-in-time">{fmtTime(att?.check_in_at, att?.check_in_local)}</div>
                                </div>
                                <div className="border border-ink/10 p-4">
                                    <div className="overline">Check-out</div>
                                    <div className="font-display text-xl mt-1" data-testid="attendance-check-out-time">{fmtTime(att?.check_out_at, att?.check_out_local)}</div>
                                </div>
                            </div>

                            <div className="mt-8 flex items-stretch sm:items-center gap-3 flex-col sm:flex-row">
                                <button
                                    onClick={() => doCheck("in")}
                                    disabled={busy || checkedIn || !isEmployee}
                                    data-testid="attendance-check-in-btn"
                                    className="inline-flex items-center justify-center gap-2 bg-klein text-white px-6 py-4 sm:py-3.5 font-medium hover:bg-klein-500 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
                                    Check in
                                </button>
                                <button
                                    onClick={() => doCheck("out")}
                                    disabled={busy || !checkedIn || checkedOut || !isEmployee}
                                    data-testid="attendance-check-out-btn"
                                    className="inline-flex items-center justify-center gap-2 border-2 border-klein text-klein px-6 py-4 sm:py-3.5 font-medium hover:bg-klein/5 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogOut className="w-4 h-4" />}
                                    Check out
                                </button>
                                <button
                                    onClick={fetchLocation}
                                    className="text-xs uppercase tracking-[0.18em] text-ink/60 hover:text-ink inline-flex items-center gap-1"
                                    data-testid="attendance-refresh-location"
                                >
                                    <MapPin className="w-3 h-3" /> Refresh location
                                </button>
                            </div>
                            {coords && (
                                <div className="mt-3 text-xs text-ink/40" data-testid="attendance-current-coords">
                                    Your location: {coords.lat.toFixed(5)}, {coords.lng.toFixed(5)} · ±{Math.round(coords.accuracy)}m
                                </div>
                            )}
                            {locError && (
                                <div className="mt-3 text-xs text-red-600" data-testid="attendance-location-error">{locError}</div>
                            )}
                            {verifyStatus && (
                                <div className="mt-4 inline-flex items-center gap-2 text-xs text-ink/70 border border-ink/10 bg-ink/[0.02] px-3 py-2" data-testid="attendance-verify-status">
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    {verifyStatus === "locating" && "Getting your location…"}
                                    {verifyStatus === "verifying" && <><ShieldCheck className="w-3.5 h-3.5" /> Verifying your identity…</>}
                                    {verifyStatus === "submitting" && "Recording attendance…"}
                                </div>
                            )}
                            {faceRequired && !verifyStatus && (
                                <div className="mt-3 text-[11px] text-ink/40 inline-flex items-center gap-1" data-testid="attendance-face-hint">
                                    <ShieldCheck className="w-3 h-3" /> Face check runs silently when you tap check-in/out.
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Worksites card — visible to all roles so employees know approved locations */}
                <div className="bg-white border border-ink/10 p-6">
                    <div className="overline">Approved worksites</div>
                    <h3 className="font-display text-lg tracking-tight mt-1">Allowed locations</h3>
                    {loading ? (
                        <div className="mt-6 text-sm text-ink/40">Loading…</div>
                    ) : worksites.length === 0 ? (
                        <div className="mt-6 text-sm text-ink/40">
                            No worksites configured yet. Your HR admin must add one in Settings before you can check in.
                        </div>
                    ) : (
                        <ul className="mt-4 space-y-3" data-testid="attendance-worksite-list">
                            {worksites.map((w) => (
                                <li key={w.id} className="border border-ink/10 p-3 text-sm" data-testid={`attendance-worksite-${w.id}`}>
                                    <div className="font-medium text-ink">{w.name}</div>
                                    {w.address && <div className="text-xs text-ink/50 mt-0.5">{w.address}</div>}
                                    <div className="text-[11px] text-ink/40 mt-1 font-mono">
                                        {w.latitude.toFixed(5)}, {w.longitude.toFixed(5)} · ±{w.radius_m}m
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}
